import AdjustRoundedIcon from '@mui/icons-material/AdjustRounded';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EventIcon from '@mui/icons-material/Event';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PriceChangeRoundedIcon from '@mui/icons-material/PriceChangeRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import TimesOneMobiledataIcon from '@mui/icons-material/TimesOneMobiledata';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  IconButton,
  List,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FormBox } from 'components/form';
import { SearchFilter } from 'components/ui/SearchFilter';
import { SingleCompanyProductView } from 'components/ui/SingleCompanyProductView';
import useWindowsDimension from 'functions/useWindowsDimension';
import { rq, rqPost } from 'http/apiRoutes';
import { useGet, usePost, usePut } from 'http/useApi';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import { ObjectDrawer } from './ObjectDrawer';
// @ts-ignore
import { getFilteredList } from 'functions/misc';

import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { GenerateGuid } from 'functions/GuidChecks';
import { IProductUpdateViewModel } from 'Interfaces/ICompaniesConfig';
import { IEmployeeViewModel } from 'Interfaces/IEmployeeConfig';
import {
  EnumObjectType,
  EnumPlacement,
  ObjectCompanyProductFilterList,
  allObjectList,
} from 'Interfaces/IGlobalTypeConfig';
import { IResourcesViewModel } from 'Interfaces/IResourcesConfig';
import { useTranslation } from 'react-i18next';
import {
  ICompanyProductUpdateViewModel,
  ICompanyProductViewModel,
} from '../../Interfaces/ICompanyProductConfig';

const initialState: ICompanyProductViewModel = {
  id: '',
  serviceTag: '',
  productId: '',
  guaranteeEndDate: null,
  product: {} as IProductUpdateViewModel,
  companyId: GenerateGuid(),
  purchaserId: GenerateGuid(),
  ownerId: null,
  owner: {} as IEmployeeViewModel,
  comment: '',
  lastBilled: null,
  productQuantity: 1,
  quantity: 1,
  unitPrice: 0,
  retailPrice: 0,
  discount: 0,
  lifetime: 0,
  security: true,
  status: 0,
  productWarning: '',
  placement: 0 as EnumPlacement,
  isActive: false,
  isBillable: true,
  serialNumber: '',
  resourceId: null,
  toggleEdit: false,
  isDateEditable: false,
  resource: {} as IResourcesViewModel,
};

type Action =
  | { type: 'TOGGLE_IS_ACTIVE'; payload: boolean }
  | { type: 'TOGGLE_EDIT'; payload: boolean }
  | { type: 'SET_DATE_EDITABLE'; payload: boolean }
  | { type: 'SET_PRODUCT'; payload: ICompanyProductViewModel }
  | {
      type: 'UPDATE_FIELD';
      field: keyof ICompanyProductViewModel;
      payload: any;
    }
  | { type: 'REMOVE_OWNER' };

const reducer = (
  state: ICompanyProductViewModel,
  action: Action
): ICompanyProductViewModel => {
  switch (action.type) {
    case 'SET_PRODUCT':
      return { ...state, ...action.payload };
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.payload };
    case 'SET_DATE_EDITABLE':
      return { ...state, isDateEditable: action.payload };
    case 'TOGGLE_EDIT':
      return { ...state, toggleEdit: action.payload };
    case 'TOGGLE_IS_ACTIVE':
      return { ...state, isActive: action.payload };
    case 'REMOVE_OWNER':
      return { ...state, owner: null as any, ownerId: null };
    default:
      return state;
  }
};

export const CompanyProducts = () => {
  const { t } = useTranslation();
  const { height } = useWindowsDimension();
  const { companyId } = useParams() as any;

  const [companyProducts, setCompanyProducts] = useState<
    ICompanyProductViewModel[]
  >([]);
  const [filteredCompanyProductList, setFilteredCompanyProductList] = useState<
    ICompanyProductViewModel[]
  >([]);
  const [state, dispatch] = useReducer(reducer, { ...initialState });
  const [employees, setEmployees] = useState([] as IEmployeeViewModel[]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [filterType, setFilterType] = useState('All');

  // API
  const isEnabled = !!companyId;
  const { data: companyProductData } = useGet(
    rq.companyProducts(companyId, isEnabled),
  );
  const { data: employeeData } = useGet(rq.employees(companyId, isEnabled));
  let employeeId = '';
  const postEmployeeDeactivate = usePost(
    rqPost.employeeDeactivate(companyId, employeeId, true)
  );
  const putEmployee = usePut(
    rqPost.employee(companyId, employeeId, true),
  );
  const productId = state.id ? state.id : '';
  const updateCompanyProducts = usePut(
    rqPost.updateCompanyProduct(productId, true)
  );

  // TYPES FOR INTERNAL JSX
  interface SwitchProps {
    onOff: boolean;
    dispatch: React.Dispatch<Action>;
  }

  const IsActiveSwitch: React.FC<SwitchProps> = ({ onOff, dispatch }) => {
    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'TOGGLE_IS_ACTIVE', payload: e.target.checked });
    };
    return (
      <Grid item xs='auto' sx={{ m: 0, p: 0 }}>
        <Stack direction='row'>
          <Typography
            sx={{
              m: 0,
              p: 2.2,
              pr: 0,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 'bold',
              fontSize: 14,
              color: 'primary.text',
            }}
          >
            {onOff
              ? t('company.companyProducts.onOffYes')
              : t('company.companyProducts.onOffNo')}
          </Typography>

          <Switch
            color={onOff ? 'secondary' : 'primary'}
            sx={{
              mt: 1,
              mr: 1,
              '& .MuiSwitch-thumb': {
                backgroundColor: onOff ? 'secondary.light' : 'primary.text',
              },
              '& .MuiSwitch-track': {
                backgroundColor: onOff
                  ? 'rgba(0, 0, 0, 0.7)'
                  : 'rgba(0, 0, 255, 0.3)',
              },
            }}
            checked={onOff}
            onChange={handleToggle}
            name={t('company.companyProducts.isCompanyProductActive')}
          />
        </Stack>
      </Grid>
    );
  };

  // HANDLERS
  const handleClick = (e: any, product: ICompanyProductViewModel) => {
    const _product = { ...product, productId: product.product.id };
    dispatch({ type: 'SET_PRODUCT', payload: _product });
  };

  const handleDeleteConfirm = () => setDeleteConfirm(false);

  type FormOrMouseEvent =
    | React.FormEvent<HTMLFormElement>
    | React.MouseEvent<HTMLDivElement, MouseEvent>;

  const handleChangeEmployee = (
    event: FormOrMouseEvent,
    selectedObject: any,
    objectType: EnumObjectType
  ) => {
    switch (objectType) {
      case EnumObjectType.CompanyEmployees: {
        const object = selectedObject as IEmployeeViewModel;
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'ownerId',
          payload: object.id,
        });
        dispatch({ type: 'UPDATE_FIELD', field: 'owner', payload: object });
        break;
      }
      default:
        break;
    }
  };

  const handleFilter = (e: any) => {
    setFilterType(e);
    let filterValue = e === 'All' ? 99 : 0;
    const filteredByPlacement = companyProducts.filter(
      (item: any) => item.placement <= filterValue
    );
    setFilteredCompanyProductList(filteredByPlacement);
  };

  const handleSearch = (e: any) => {
    setFilteredCompanyProductList(
      getFilteredList(companyProductData?.companyProducts, e.target.value)
    );
  };

  // EFFECTS
  useEffect(() => {
    if (companyProductData?.companyProducts?.length > 0) {
      allObjectList[5].objectList = companyProductData.companyProducts;

      dispatch({
        type: 'UPDATE_FIELD',
        field: 'quantity',
        payload: state.quantity,
      });

      setCompanyProducts(companyProductData.companyProducts);
      setFilteredCompanyProductList(companyProductData.companyProducts);

      if (companyProductData.companyProducts[0].guaranteeEndDate) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'guaranteeEndDate',
          payload: companyProductData.companyProducts[0].guaranteeEndDate,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProductData]);

  useEffect(() => {
    if (employeeData?.allEmployees) {
      allObjectList[3].objectList = employeeData.allEmployees;
      setEmployees(employeeData.allEmployees);
    }
  }, [employeeData]);

  const onSubmit = (e: any) => {
    e.preventDefault();
    const companyProduct: ICompanyProductUpdateViewModel = {
      id: state.id || '',
      serviceTag: state.serviceTag,
      productId: state.product.id,
      product: state.product,
      productQuantity: +state.productQuantity,
      quantity: +state.quantity,
      companyId: state.companyId,
      comment: state.comment,
      unitPrice: state.unitPrice,
      retailPrice: state.retailPrice,
      discount: state.discount,
      lifetime: state.lifetime,
      security: state.security,
      status: state.status,
      productWarning: state.productWarning,
      guaranteeEndDate: state.guaranteeEndDate,
      purchaserId: state.purchaserId,
      ownerId: state.ownerId,
      placement: state.placement,
      isActive: state.isActive,
      serialNumber: state.serialNumber,
      resourceId: state.resourceId,
    };
    updateCompanyProducts.mutate(companyProduct, {
      onSuccess: () => {
        console.log('updateCompanyProducts', companyProduct);
      },
    });
  };

  // --- Gruppér produkter pr. produktnavn (typet korrekt for TS) ---
  const groupedEntries = useMemo<[string, ICompanyProductViewModel[]][]>(() => {
    const buckets = filteredCompanyProductList.reduce<
      Record<string, ICompanyProductViewModel[]>
    >((acc, cp) => {
      const key = cp.product?.name ?? 'Uten navn';
      (acc[key] ??= []).push(cp);
      return acc;
    }, {});
    return Object.entries(buckets) as [string, ICompanyProductViewModel[]][];
  }, [filteredCompanyProductList]);

  console.log('PRODUCTS STATE', state);

  return (
    <Grid container>
      {/* LEFT SIDE */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          borderRadius: 3,
          pr: 0.5,
        }}
      >
        <Grid item xs={12} sx={{ p: 0, m: 0 }}>
          <SearchFilter
            Icon={SupportAgentRoundedIcon}
            Header={t('company.companyProducts.header')}
            filterValue={filterType}
            handleFilterChange={handleFilter}
            selectList={ObjectCompanyProductFilterList}
            handleSearch={handleSearch}
            hasSelectAll={true}
          />

          <List
            sx={{
              height: Math.ceil(height - 242),
              width: '100%',
              mb: 0,
              p: 2,
              backgroundColor: 'primary.back',
              overflow: 'auto',
            }}
          >
            {filteredCompanyProductList?.length === 0 && <div>No data</div>}

            {/* Accordions gruppert på product.name */}
            {groupedEntries.map(([productName, group]) => (
              <Accordion
                key={productName}
                disableGutters
                sx={{
                  mb: 1,
                  backgroundColor: 'primary.main',
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow:
                    '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      m: 0,
                    },
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      textTransform: 'uppercase',
                      color: 'primary.text',
                    }}
                  >
                    {productName}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails
                  sx={{ backgroundColor: 'primary.back', p: 0 }}
                >
                  {group.map((companyProduct, index) => (
                    <div
                      key={`${companyProduct.id}-${index}`}
                      onClick={(e: any) => handleClick(e, companyProduct)}
                      style={{ cursor: 'pointer' }}
                    >
                      <SingleCompanyProductView
                        companyProduct={companyProduct}
                        quantity={companyProduct.quantity}
                      />
                    </div>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </List>

          <Paper
            variant='elevation3'
            sx={{
              p: 2,
              height: 64,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }}
          ></Paper>
        </Grid>
      </Grid>

      {/* RIGHT SIDE */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          borderRadius: 3,
          pl: 0.5,
        }}
      >
        <Grid item xs={12} sx={{ p: 0, m: 0 }}>
          <SearchFilter
            Icon={SupportAgentRoundedIcon}
            Header={t('company.companyProducts.header')}
          />

          <List
            sx={{
              height: Math.ceil(height - 242),
              width: '100%',
              mb: 0,
              p: 2,
              backgroundColor: 'primary.back',
              overflow: 'auto',
            }}
          >
            {state.product.name?.length > 0 && (
              <Grid item xs={12}>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      height: Math.ceil(height - 850),
                      backgroundColor: 'primary.main',
                      borderRadius: 2,
                      boxShadow:
                        '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                    }}
                  >
                    <FormBox
                      Icon={PersonRoundedIcon}
                      title={t('company.companyProducts.ownerHeader')}
                      FormJSX={() => (
                        <Grid
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            position: 'absolute',
                            right: 30,
                            justifyContent: 'flex-end',
                            width: 26,
                          }}
                        >
                          <ObjectDrawer
                            buttonContext={t(
                              'company.companyProducts.selectEmployee'
                            )}
                            buttonIcon={
                              <ChangeCircleIcon sx={{ fontSize: 26 }} />
                            }
                            objectList={allObjectList}
                            objectType={[EnumObjectType.CompanyEmployees]}
                            handleChange={handleChangeEmployee}
                            buttonWidth={26}
                            buttonHeight={26}
                          />
                          <IconButton
                            sx={{ m: 0, p: 0 }}
                            onClick={() => dispatch({ type: 'REMOVE_OWNER' })}
                          >
                            <PersonRemoveOutlinedIcon />
                          </IconButton>
                        </Grid>
                      )}
                    />

                    {state.product.name?.length > 0 && (
                      <Grid item xs={12} sx={{ p: 1 }}>
                        <SingleCompanyProductView
                          companyProduct={state}
                          quantity={state.quantity}
                        />
                      </Grid>
                    )}
                  </Paper>
                </Grid>

                {/* quantity */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <TimesOneMobiledataIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        Quantity
                      </Typography>
                    </Grid>
                    <TextField
                      sx={{
                        width: 350,
                        pr: 2,
                        pt: 1.5,
                        '& .Mui-focused': {
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        },
                      }}
                      InputProps={{
                        inputProps: {
                          style: { textAlign: 'right', paddingRight: 10 },
                        },
                      }}
                      value={state.quantity || ''}
                      onChange={(e: any) =>
                        dispatch({
                          type: 'UPDATE_FIELD',
                          field: 'quantity',
                          payload: e.target.value,
                        })
                      }
                      onFocus={(e) => e.target.select()}
                    />
                  </Grid>
                </Paper>

                {/* serviceTag */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <EditNoteIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        {t('company.companyProducts.serviceTag')}
                      </Typography>
                    </Grid>
                    <TextField
                      sx={{
                        width: 350,
                        pr: 2,
                        pt: 1.5,
                        '& .Mui-focused': {
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        },
                      }}
                      value={state.serviceTag || ''}
                      onChange={(e: any) =>
                        dispatch({
                          type: 'UPDATE_FIELD',
                          field: 'serviceTag',
                          payload: e.target.value,
                        })
                      }
                      onFocus={(e) => e.target.select()}
                    />
                  </Grid>
                </Paper>

                {/* warranty end date */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <EventIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        {t('company.companyProducts.warrantyEndDate')}
                      </Typography>
                    </Grid>
                    {state?.guaranteeEndDate || state.isDateEditable ? (
                      <Grid sx={{ pr: 2, pt: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            value={
                              state.guaranteeEndDate
                                ? dayjs(new Date(state.guaranteeEndDate))
                                : null
                            }
                            onChange={(newValue) => {
                              dispatch({
                                type: 'UPDATE_FIELD',
                                field: 'guaranteeEndDate',
                                payload: newValue,
                              });
                              dispatch({
                                type: 'SET_DATE_EDITABLE',
                                payload: false,
                              });
                            }}
                            views={['year', 'month', 'day']}
                          />
                        </LocalizationProvider>
                      </Grid>
                    ) : (
                      <Button
                        variant='contained'
                        sx={{ mr: 2 }}
                        onClick={() =>
                          dispatch({ type: 'SET_DATE_EDITABLE', payload: true })
                        }
                      >
                        {t('company.companyProducts.addWarantyEndDate')}
                      </Button>
                    )}
                  </Grid>
                </Paper>

                {/* retailPrice */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <PriceChangeRoundedIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        {t('company.companyProducts.retailPrice')}
                      </Typography>
                    </Grid>
                    <Button
                      variant='contained'
                      sx={{ mr: 2 }}
                      onClick={() =>
                        dispatch({
                          type: 'TOGGLE_EDIT',
                          payload: !state.toggleEdit,
                        })
                      }
                    >
                      {t('company.companyProducts.editValue')}
                    </Button>
                    <TextField
                      sx={{
                        width: 350,
                        pr: 2,
                        pt: 1.5,
                        '& .Mui-focused': {
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        },
                      }}
                      value={state.retailPrice || ''}
                      InputProps={{
                        inputProps: {
                          style: { textAlign: 'right', paddingRight: 10 },
                        },
                      }}
                      disabled={!state.toggleEdit}
                      onChange={(e: any) =>
                        dispatch({
                          type: 'UPDATE_FIELD',
                          field: 'retailPrice',
                          payload: e.target.value,
                        })
                      }
                      onFocus={(e) => e.target.select()}
                    />
                  </Grid>
                </Paper>

                {/* Quantity (nr. 2) */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <PriceChangeRoundedIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        {t('company.companyProducts.quantity')}
                      </Typography>
                    </Grid>

                    <TextField
                      sx={{
                        width: 350,
                        pr: 2,
                        pt: 1.5,
                        '& .Mui-focused': {
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        },
                      }}
                      value={state.quantity}
                      onChange={(e: any) =>
                        dispatch({
                          type: 'UPDATE_FIELD',
                          field: 'quantity',
                          payload: Number(e.target.value),
                        })
                      }
                      onFocus={(e) => e.target.select()}
                    />
                  </Grid>
                </Paper>

                {/* Comment */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <PriceChangeRoundedIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        {t('company.companyProducts.comment')}
                      </Typography>
                    </Grid>

                    <TextField
                      sx={{
                        width: 500,
                        p: 1,
                        '& .Mui-focused': {
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        },
                        '& .MuiInputLabel-root': { display: 'none' },
                        '& .MuiInputBase-root': {
                          marginTop: 0,
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                        '& .MuiFilledInput-root': {
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      }}
                      variant='filled'
                      multiline
                      maxRows={2}
                      minRows={2}
                      label={null}
                      value={state.comment}
                      onChange={(e: any) =>
                        dispatch({
                          type: 'UPDATE_FIELD',
                          field: 'comment',
                          payload: e.target.value,
                        })
                      }
                      onFocus={(e) => e.target.select()}
                    />
                  </Grid>
                </Paper>

                {/* Product warning */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <ReportProblemRoundedIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        {t('company.companyProducts.productWarning')}
                      </Typography>
                    </Grid>
                    <TextField
                      sx={{
                        width: 500,
                        p: 1,
                        '& .Mui-focused': {
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        },
                        '& .MuiInputLabel-root': { display: 'none' },
                        '& .MuiInputBase-root': {
                          marginTop: 0,
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                        '& .MuiFilledInput-root': {
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      }}
                      variant='filled'
                      multiline
                      maxRows={2}
                      minRows={2}
                      label={null}
                      value={state.productWarning || ''}
                      onChange={(e: any) =>
                        dispatch({
                          type: 'UPDATE_FIELD',
                          field: 'productWarning',
                          payload: e.target.value,
                        })
                      }
                      onFocus={(e) => e.target.select()}
                    />
                  </Grid>
                </Paper>

                {/* Active */}
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow:
                      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 25%), 0px 1px 8px 0px rgb(0 0 0 / 10%)',
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <Grid item xs='auto' sx={{ pl: 2, pt: 1.5 }}>
                      <AdjustRoundedIcon sx={{ fontSize: 32 }} />
                    </Grid>
                    <Grid item xs='auto' sx={{ pl: 1, pt: 2.2, flexGrow: 1 }}>
                      <Typography
                        sx={{
                          textTransform: 'uppercase',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 'bold',
                          fontSize: 14,
                          letterSpacing: 2,
                        }}
                      >
                        {t('company.companyProducts.active')}
                      </Typography>
                    </Grid>
                    <IsActiveSwitch
                      onOff={state.isActive}
                      dispatch={dispatch}
                    />
                  </Grid>
                </Paper>
              </Grid>
            )}
          </List>

          <Paper
            variant='elevation3'
            sx={{
              p: 2,
              height: 64,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }}
          >
            {/*   Submit Button */}
            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row' }}>
              {state.isActive === false && (
                <Button
                  variant='contained'
                  onClick={() => setDeleteConfirm(!deleteConfirm)}
                >
                  {postEmployeeDeactivate.isLoading
                    ? `${t('company.companyProducts.deletingCompanyProduct')}`
                    : `${t('company.companyProducts.deleteCompanyProduct')}`}
                </Button>
              )}
              {!!deleteConfirm === true && (
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={handleDeleteConfirm}
                >
                  {t('company.companyProducts.confirmDelete')}
                </Button>
              )}
              <Grid sx={{ flexGrow: 1 }} />
              <Button onClick={onSubmit} variant='contained'>
                {putEmployee.isLoading
                  ? `${t('company.companyProducts.updatingCompanyProduct')}`
                  : `${t('company.companyProducts.updateCompanyProduct')}`}
              </Button>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
};
